import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * EV-493（PM 2026-10-28）。一覧の1ページの件数（20・50・100、初めは 50）。選んだ件数は localStorage に
 * 一覧ごとのキーで覚え、読めない・候補に無い値なら 50（private window などで localStorage が throw しても）。
 * 配送先・運転者・機器・車両の一覧で同じ部品を使う。
 */
export const PAGE_SIZES = [20, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 50;

export const readPageSize = (storageKey: string): number => {
  try {
    const stored = Number(window.localStorage.getItem(storageKey));
    return (PAGE_SIZES as readonly number[]).includes(stored) ? stored : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
};

export const writePageSize = (storageKey: string, size: number) => {
  try {
    window.localStorage.setItem(storageKey, String(size));
  } catch {
    // 覚えられなくても、この画面ではその件数で表示する。
  }
};

/** 覚えた件数と、選んで覚える関数。 */
export function usePageSize(storageKey: string): [number, (size: number) => void] {
  const [pageSize, setPageSizeState] = useState<number>(() => readPageSize(storageKey));
  const setPageSize = useCallback(
    (size: number) => {
      writePageSize(storageKey, size);
      setPageSizeState(size);
    },
    [storageKey]
  );
  return [pageSize, setPageSize];
}

interface PageSizeSelectProps {
  id: string;
  value: number;
  onChange: (size: number) => void;
}

/** 「表示件数」のドロップダウン（一覧の右下、ページ送りの上）。 */
export const PageSizeSelect: React.FC<PageSizeSelectProps> = ({ id, value, onChange }) => {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-row items-center justify-end gap-2">
      <label htmlFor={id} className="text-neutral/100">
        {t('ui.page-size', { ns: 'common' })}
      </label>
      <select
        id={id}
        className="rounded-md border px-2 py-1"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {PAGE_SIZES.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
  );
};
